import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Grid,
  IconButton,
  Typography,
  Card,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";

import { useDeleteMutation } from "slices/api";
import { useMasterData } from "hooks/useMasterData";

import SelectField from "Components/Common/SelectField";
import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import { masterDataIds, locationMasterDataTypes } from "common/constant";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import EditMasterDataModal from "./EditMasterDataModal";
import Tooltip from "Components/Common/Tooltip";

const nonDeletableTypes = [
  masterDataIds["Applicant Type"],
  masterDataIds["Premise Type"],
  masterDataIds["Premise Area"],
  masterDataIds["Application Status"],
  masterDataIds["Application Type"],
  masterDataIds["Tariff"],
  masterDataIds["Utility"],
];

const MasterData = ({ t }) => {
  const [editItem, setEditItem] = useState({});
  const [deleteItemId, setDeleteItemId] = useState("");
  const [isDeleteButtonClicked, setIsDeleteButtonClicked] = useState(false);

  const [deleteMasterDataById, { isLoading: isDeletingData }] =
    useDeleteMutation();

  const [selectedValues, setSelectValues] = useState({
    selectedTypeId: masterDataIds["Application Type"],
    premiseAreaId: 1,
    zoneId: 1,
    blockId: 1,
    buildingId: 1,
  });

  const { selectedTypeId, premiseAreaId, zoneId, blockId, buildingId } =
    selectedValues;

  useEffect(() => {
    setEditItem({});
  }, [selectedTypeId]);

  const {
    selectedMasterDataType,
    columns,
    baseTableProps,
    masterData,
    isLoading,
  } = useMasterData({
    selectedTypeId,
    premiseAreaId,
    zoneId,
    blockId,
    buildingId,
    t,
  });

  const {
    id: selectedMasterDataId,
    updateDataEndpoint,
    endpoint,
    tags,
    label,
    dependedFieldsForGetData,
  } = selectedMasterDataType;

  const getEndpoint = useCallback(
    (id) => {
      if (locationMasterDataTypes.includes(selectedMasterDataId)) {
        return `${updateDataEndpoint}/${id}/location`;
      } else {
        return `${updateDataEndpoint ?? endpoint}/${id}`;
      }
    },
    [updateDataEndpoint, endpoint]
  );

  const tableColumns = useMemo(
    () => [
      ...columns,
      {
        title: "Actions",
        renderCell: (item) => {
          return (
            <div className="d-flex gap-2">
              <Tooltip title={t("Edit")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditItem(item);
                  }}
                  color="primary"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!nonDeletableTypes.includes(selectedTypeId) && (
                <Tooltip title={t("Delete")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDeleteItemId(item.id);
                      setIsDeleteButtonClicked(true);
                    }}
                    color="primary"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          );
        },
      },
    ],
    [selectedMasterDataType]
  );

  const isMasterDataAddable = useMemo(
    () => !(isEmpty(editItem) && nonDeletableTypes.includes(selectedTypeId)),
    [selectedTypeId, editItem]
  );

  return (
    <>
      {isDeleteButtonClicked && (
        <ConfirmationDialog
          message="Are you sure you want to delete?"
          onCancel={() => setIsDeleteButtonClicked(false)}
          onConfirm={async () => {
            await deleteMasterDataById({
              endpoint: getEndpoint(deleteItemId),
              tags: tags,
              toastMessage: `${label} deleted successfully`,
            });
            setDeleteItemId("");
            setIsDeleteButtonClicked(false);
          }}
          isOpen={isDeleteButtonClicked}
          title="Warning"
          variant="warning"
          isLoading={isDeletingData}
        />
      )}
      <div className="h-100">
        <BreadCrumb title={t("Master Data")} pageTitle={t("Dashboard")} />

        <Grid container columnSpacing={3} rowSpacing={2}>
          <Grid item xs={12} sm={6} order={{ xs: 2, sm: 1 }}>
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "calc(100vh - 230px)",
                }}
              >
                <CircularProgress size={30} />
              </div>
            ) : (
              <BaseTable
                columns={tableColumns}
                {...baseTableProps}
                maxHeight="calc(100vh - 230px)"
              />
            )}
          </Grid>
          <Grid item sm={6} xs={12} order={{ xs: 1, sm: 2 }} className="mt-1">
            <Card className="px-3 pt-3">
              <Grid item xs={12} md={6}>
                <SelectField
                  label={t("Select Type")}
                  onChange={(_, { value }) =>
                    setSelectValues((prev) => ({
                      ...prev,
                      selectedTypeId: value,
                    }))
                  }
                  value={
                    masterData.find(({ id }) => id === selectedTypeId).label ??
                    ""
                  }
                  options={masterData}
                  getOptionLabel={({ label }) => label}
                  getOptionValue={({ id }) => id}
                />
              </Grid>
              <Grid container item md={12} columnSpacing={2}>
                {dependedFieldsForGetData?.map(
                  ({ fieldKey, options, fieldLabel }) => (
                    <Grid item xs={12} md={6} key={fieldKey}>
                      <SelectField
                        label={t(`${fieldLabel}`)}
                        options={options}
                        onChange={(_, { value }) => {
                          setSelectValues((prev) => ({
                            ...prev,
                            [fieldKey]: value,
                          }));
                        }}
                        value={
                          options.length
                            ? options
                                .map(({ id, name }) => ({
                                  label: name,
                                  value: id,
                                }))
                                .find(
                                  ({ value }) =>
                                    value === selectedValues[fieldKey]
                                )
                            : ""
                        }
                        getOptionLabel={({ name }) => name}
                        getOptionValue={({ id }) => id}
                      />
                    </Grid>
                  )
                )}
              </Grid>
              <Grid xs={12} item className="mt-2">
                <Typography variant="subtitle1" fontWeight="bold">
                  {isEmpty(editItem) ? t(`Add ${label}`) : t(`Edit ${label}`)}
                  {!isMasterDataAddable && (
                    <Tooltip title={t(`You can not add new ${label}`)}>
                      <InfoIcon fontSize="small" className="cursor-pointer" />
                    </Tooltip>
                  )}
                </Typography>
              </Grid>
              <EditMasterDataModal
                selectedMasterDataType={selectedMasterDataType}
                editItem={editItem}
                isMasterDataAddable={!isMasterDataAddable}
                setEditItem={setEditItem}
                getEndpoint={getEndpoint}
              />
            </Card>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

MasterData.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(MasterData);
